import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSiteContext } from "../../context/SiteContext";
import pollServices from "../../services/pollServices";

const PollResults = ({ onClose }) => {
    const { pollResults, setPollResults } = useSiteContext();
    const [selectedTab, setSelectedTab] = useState("total");
    const pollIntervalRef = useRef(null);


    useEffect(() => {
        setSelectedTab("total");
    }, [pollResults?.id, pollResults?.lastUpdated]);

    // Poll the database every 3 seconds for updated results
    // This ensures results display even if PubSub messages are missed
    const pollResultsIdRef = useRef(pollResults?.id);
    const pollResultsSnapshotRef = useRef(pollResults);
    useEffect(() => {
        pollResultsIdRef.current = pollResults?.id;
        pollResultsSnapshotRef.current = pollResults;
    }, [pollResults]);

    const fetchResultsFromDB = useCallback(async () => {
        const currentId = pollResultsIdRef.current;
        const currentResults = pollResultsSnapshotRef.current;
        if (!currentId) return;
        try {
            const pollDoc = await pollServices.getPollById(currentId);
            const data = pollDoc?.data || pollDoc;
            if (!data || !data.options) return;

            const dbTotalVotes = data.options.reduce((sum, opt) => sum + (opt.votes || 0), 0);
            const currentTotalVotes = currentResults?.totalVotes || 0;
            const currentVoterCount = currentResults?.votes?.length || 0;

            // Fetch voter details by pollId only (no meetingId/sessionId needed)
            let dbVoters = null;
            try {
                const res = await pollServices.getPollVoters(currentId);
                const voters = res?.data?.voters || [];
                if (voters.length > 0) {
                    dbVoters = voters;
                }
            } catch (_) { /* voter details are optional */ }

            const hasNewerVotes = dbTotalVotes > currentTotalVotes;
            const hasMoreVoters = dbVoters && dbVoters.length > currentVoterCount;

            // Only update if the DB has more data than what we currently show
            if (hasNewerVotes || hasMoreVoters) {
                // Merge DB vote counts into existing pollResults (preserve poll_type, appearance, etc.)
                const updatedOptions = (currentResults?.options || []).map((opt, idx) => ({
                    ...opt,
                    votes: data.options[idx]?.votes ?? opt.votes ?? 0,
                }));

                setPollResults({
                    ...currentResults,
                    options: updatedOptions,
                    totalVotes: Math.max(dbTotalVotes, currentTotalVotes),
                    votes: hasMoreVoters ? dbVoters : (currentResults?.votes || []),
                    lastUpdated: Date.now(),
                });
            }
        } catch (err) {
            console.warn("PollResults - failed to fetch from DB:", err);
        }
    }, [setPollResults]);

    useEffect(() => {
        if (!pollResults?.id) return;
        // Delay initial fetch by 2s to avoid hammering the server during poll activation cascade
        const initialTimeout = setTimeout(fetchResultsFromDB, 2000);
        // Then poll every 5 seconds (reduced frequency to ease mobile performance)
        pollIntervalRef.current = setInterval(fetchResultsFromDB, 5000);
        return () => {
            clearTimeout(initialTimeout);
            if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
            }
        };
    }, [pollResults?.id]);

    if (!pollResults) {
        return null;
    }

    // Get poll type and settings
    // Also detect open_text polls that were created before poll_type was properly stored
    let pollType = pollResults.poll_type || 'multiple_choice';

    // Fallback detection for open_text polls created before poll_type was properly stored
    // NOTE: Do NOT check open_text_settings — PollModal.js always includes it for all poll types
    if (pollType === 'multiple_choice') {
        const options = pollResults.options || [];

        // Check for placeholder options "Response 1/2" (legacy open_text polls)
        const hasPlaceholderOptions = options.length === 2 &&
            options.every(opt => {
                const text = typeof opt === 'string' ? opt : (opt?.text || '');
                return text === 'Response 1' || text === 'Response 2';
            });

        // Check for options with empty text (created before fix)
        const hasEmptyTextOptions = options.length === 2 &&
            options.every(opt => {
                const text = typeof opt === 'string' ? opt : (opt?.text || '');
                return text === '' || text.trim() === '';
            });

        // Check for empty options array (open_text polls should have no options)
        const hasNoOptions = options.length === 0;

        if (hasPlaceholderOptions || hasEmptyTextOptions || hasNoOptions) {
            pollType = 'open_text';
        }
    }

    const isRatingPoll = pollType === 'rating';
    const isOpenTextPoll = pollType === 'open_text';
    const ratingSettings = pollResults.rating_settings || {};
    const accentColor = pollResults.appearance?.accent_color || '#7700ff';

    const totalVotes = pollResults.totalVotes ?? pollResults.votes?.length ?? 0;
    const skippedEntries = pollResults.skipped || [];
    const totalSkipped =
        pollResults.skippedCount ?? skippedEntries.length ?? 0;
    const totalResponses = totalVotes + totalSkipped;

    const getPercentage = (votes) => {
        if (totalVotes === 0) return 0;
        return Math.round((votes / totalVotes) * 100);
    };

    const getVotersForOption = (optionIndex) => {
        if (!pollResults.votes || optionIndex === -1) return [];
        return pollResults.votes.filter((vote) =>
            vote.optionIndices.includes(optionIndex)
        );
    };

    // Helper to extract option text properly
    const getOptionText = (option) => {
        if (!option) return 'Unknown';
        if (typeof option.text === 'string') return option.text;
        if (typeof option.label === 'string') return option.label;
        if (option.value !== undefined) return String(option.value);
        return 'Unknown';
    };

    const renderVotersList = () => {
        if (selectedTab === "total") {
            // For open text polls, show text responses
            if (isOpenTextPoll) {
                return (
                    <div className="space-y-3">
                        {pollResults.votes?.map((vote, index) => (
                            <div key={index} className="p-3 bg-[#1a1a2e] rounded-lg border border-gray-700">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="text-white text-sm font-medium">{vote.participantName}</span>
                                </div>
                                <p className="text-gray-300 text-sm">{vote.textResponse || 'No response'}</p>
                            </div>
                        ))}
                    </div>
                );
            }
            return (
                <div className="space-y-2">
                    {pollResults.votes?.map((vote, index) => (
                        <div key={index} className="flex items-start gap-2">
                            <span className="text-white text-sm">{vote.participantName}</span>
                            <span className="text-gray-400 text-xs">
                                {vote.optionIndices
                                    .map((idx) => getOptionText(pollResults.options[idx]))
                                    .join(", ")}
                            </span>
                        </div>
                    ))}
                </div>
            );
        }

        if (selectedTab === "SKIPPED") {
            if (totalSkipped === 0) {
                return (
                    <p className="text-gray-500 text-sm">
                        No participants skipped this poll.
                    </p>
                );
            }

            return (
                <div className="space-y-2">
                    {skippedEntries.map((entry, index) => (
                        <div key={index} className="flex items-start gap-2">
                            <span className="text-white text-sm">
                                {entry.participantName}
                            </span>
                            <span className="text-gray-400 text-xs">Skipped</span>
                        </div>
                    ))}
                </div>
            );
        }

        // Handle rating poll tabs (rating_0, rating_1, etc.) and standard tabs (A, B, C, etc.)
        let optionIndex;
        if (selectedTab.startsWith('rating_')) {
            optionIndex = parseInt(selectedTab.replace('rating_', ''), 10);
        } else {
            optionIndex = [
                "a",
                "b",
                "c",
                "d",
                "e",
                "f",
                "g",
                "h",
                "i",
                "j",
            ].indexOf(selectedTab.toLowerCase());
        }
        const voters = getVotersForOption(optionIndex);

        if (voters.length === 0) {
            return (
                <p className="text-gray-500 text-sm">
                    No votes recorded for this option yet.
                </p>
            );
        }

        return (
            <div className="space-y-2">
                {voters.map((vote, index) => (
                    <div key={index} className="flex items-start gap-2">
                        <span className="text-white text-sm">{vote.participantName}</span>
                        <span className="text-gray-400 text-xs">
                            {getOptionText(pollResults.options[optionIndex])}
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="fixed inset-x-2 top-16 sm:inset-x-auto sm:left-4 sm:top-20 w-auto sm:w-80 bg-[#0a0a1f] border border-gray-700 rounded-xl shadow-2xl z-40 max-h-[calc(100vh-80px)] sm:max-h-[calc(100vh-120px)] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
                <h3 className="text-white text-sm font-bold tracking-wider uppercase">
                    Poll Control
                </h3>
                <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-white transition-colors"
                >
                    <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>
            </div>

            {/* Poll Question */}
            <div className="px-4 py-3 border-b border-gray-700">
                <p className="text-white text-sm font-medium">{pollResults.question}</p>
            </div>

            {/* Options with Results */}
            <div className="px-4 py-3 space-y-2 border-b border-gray-700">
                {isOpenTextPoll ? (
                    /* Open Text Poll - Show info message */
                    <div className="text-center py-2">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span className="text-gray-400 text-sm">Open Text Responses</span>
                        </div>
                        <p className="text-gray-500 text-xs">Participants can submit free-form text responses</p>
                    </div>
                ) : isRatingPoll ? (
                    /* Rating Poll - Show as rating scale */
                    <>
                        {/* Rating scale display */}
                        <div className="flex justify-between items-end gap-1 mb-3">
                            {pollResults.options?.map((option, index) => {
                                const percentage = getPercentage(option.votes);
                                const value = option.value || index + 1;
                                return (
                                    <div key={index} className="flex flex-col items-center flex-1">
                                        <div
                                            className="w-full bg-gray-800 rounded-t-sm transition-all duration-500"
                                            style={{
                                                height: `${Math.max(percentage * 0.6, 4)}px`,
                                                backgroundColor: percentage > 0 ? accentColor : undefined
                                            }}
                                        />
                                        <span className="text-white text-xs font-medium mt-1">{value}</span>
                                        <span className="text-gray-400 text-[10px]">{option.votes}</span>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Rating labels */}
                        <div className="flex justify-between text-gray-500 text-xs">
                            <span>{ratingSettings.min_label || 'Poor'}</span>
                            <span>{ratingSettings.max_label || 'Excellent'}</span>
                        </div>
                        {/* Average rating */}
                        {totalVotes > 0 && (
                            <div className="text-center mt-2">
                                <span className="text-white text-lg font-bold" style={{ color: accentColor }}>
                                    {(pollResults.options?.reduce((sum, opt, idx) =>
                                        sum + (opt.votes * (opt.value || idx + 1)), 0) / totalVotes).toFixed(1)}
                                </span>
                                <span className="text-gray-400 text-xs ml-1">avg rating</span>
                            </div>
                        )}
                    </>
                ) : (
                    /* Standard poll options */
                    pollResults.options?.map((option, index) => {
                        const percentage = getPercentage(option.votes);
                        const optionLabel = String.fromCharCode(65 + index); // A, B, C, D...

                        // Extract option text properly - handle various formats
                        let optionText = '';
                        if (typeof option.text === 'string') {
                            optionText = option.text;
                        } else if (typeof option.label === 'string') {
                            optionText = option.label;
                        } else if (option.value !== undefined) {
                            optionText = String(option.value);
                        } else {
                            optionText = `Option ${index + 1}`;
                        }

                        return (
                            <div key={index} className="space-y-1">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <span className="text-gray-400 text-xs font-medium">
                                            {optionLabel}
                                        </span>
                                        <span className="text-white text-xs truncate">
                                            {optionText}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className="text-white text-xs font-medium">
                                            {option.votes}
                                        </span>
                                        <span className="text-white text-sm font-bold min-w-[45px] text-right">
                                            {percentage}%
                                        </span>
                                    </div>
                                </div>
                                <div className="w-full bg-gray-800 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{ width: `${percentage}%`, backgroundColor: accentColor }}
                                    />
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Vote Count */}
            <div className="px-4 py-3 border-b border-gray-700">
                <p className="text-gray-400 text-xs text-center tracking-wider uppercase">
                    Responses: {totalResponses} · Votes: {totalVotes}
                    {totalSkipped ? ` · Skipped: ${totalSkipped}` : ""}
                </p>
            </div>

            {/* Tabs */}
            <div className="px-4 py-3 border-b border-gray-700">
                <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                    <button
                        onClick={() => setSelectedTab("total")}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs font-medium transition-colors`}
                        style={selectedTab === "total"
                            ? { backgroundColor: accentColor, color: 'white' }
                            : { backgroundColor: '#1f2937', color: '#9ca3af' }}
                    >
                        {isOpenTextPoll ? 'RESPONSES' : 'TOTAL'}
                    </button>
                    {/* Don't show option tabs for open text polls */}
                    {!isOpenTextPoll && pollResults.options?.map((option, index) => {
                        // Use number for rating polls, letter for others
                        const optionLabel = isRatingPoll
                            ? String(option.value || index + 1)
                            : String.fromCharCode(65 + index);
                        const tabKey = isRatingPoll ? `rating_${index}` : optionLabel;
                        return (
                            <button
                                key={index}
                                onClick={() => setSelectedTab(tabKey)}
                                className={`min-w-[36px] sm:min-w-[40px] h-8 sm:h-10 px-1.5 sm:px-2 rounded-lg text-xs font-medium transition-colors`}
                                style={selectedTab === tabKey
                                    ? { backgroundColor: accentColor, color: 'white' }
                                    : { backgroundColor: '#1f2937', color: '#9ca3af' }}
                            >
                                {optionLabel}
                            </button>
                        );
                    })}
                    {totalSkipped > 0 && (
                        <button
                            onClick={() => setSelectedTab("SKIPPED")}
                            className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs font-medium transition-colors`}
                            style={selectedTab === "SKIPPED"
                                ? { backgroundColor: accentColor, color: 'white' }
                                : { backgroundColor: '#1f2937', color: '#9ca3af' }}
                        >
                            SKIPPED
                        </button>
                    )}
                </div>
            </div>

            {/* Voters Section */}
            <div className="flex-1 overflow-y-auto">
                <div className="px-4 py-3">
                    <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">
                        {isOpenTextPoll ? 'Responses' : 'Voters'}
                    </p>
                    {totalResponses === 0 ? (
                        <p className="text-gray-500 text-sm text-center py-8">
                            No responses yet
                        </p>
                    ) : (
                        renderVotersList()
                    )}
                </div>
            </div>
        </div>
    );
};

export default PollResults;

